/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, {
  FC,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Controller,
  FieldValues,
  FormProvider,
  useForm,
  useFormState,
} from 'react-hook-form';
import { PageContext } from '@agria/theme-brochureware/src/context/pageContext';
import { Typography, TypographyComponents } from '../typography/Typography';
import { Divider } from '../divider/Divider';
import { umbracoForm } from './UmbracoFormTypes';
import { Button } from '../button';
import { Stack } from '../stack/Stack';
import { Input, InputProps } from '../form/input/Input';
import { Checkbox } from '../form/checkbox/Checkbox';
import { CrossCircleOutline, TickCircle } from '../icon';
import { Textarea } from '../form/textarea/Textarea';
import {
  ButtonSelect,
  ButtonSelectOptions,
} from '../form/buttonSelect/ButtonSelect';
import { Html } from '../html/Html';
import { FilterInput } from '../form/filterInput/FilterInput';
import { FormField } from '../form/formField/FormField';
import { InlineCalendar } from '../form/inlineCalendar/InlineCalendar';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { useGoogleReCaptchaV3 } from './useGoogleReCaptchaV3';
import { FormDataResponse } from './formAction';

type FormValues = Record<
  string,
  string | number | readonly string[] | undefined
>;

type FormErrors = umbracoForm['schemas']['HttpValidationProblemDetails'];

interface FormData {
  [key: string]: any;
}

export interface UmbracoFormProps {
  // formData?: Queries.UmbracoForm;
  formData: umbracoForm['schemas']['FormDto'];
}

type StyleFieldsetColumnProps = {
  width?: number;
};

// Styles

const StyledUmbracoForm = styled.div`
  max-width: 736px;
  margin-right: auto;
  margin-left: auto;
`;

const StyledFieldset = styled.fieldset`
  ${({ theme: { space } }) => css`
    padding: ${space.large} 0;
  `};
`;

const StyledFieldsetColumns = styled.div`
  ${({ theme: { media, space } }) => css`
    display: grid;
    gap: ${space.small};
    padding: ${space.medium} 0;

    & + & {
      padding-top: 0;
    }

    &:last-of-type {
      padding-bottom: 0;
    }

    @media (min-width: ${media.medium}) {
      grid-template-columns: repeat(12, 1fr);
    }
  `};
`;

const StyleFieldsetColumn = styled.div<StyleFieldsetColumnProps>`
  ${({ theme: { media }, width }) => css`
    @media (min-width: ${media.medium}) {
      grid-column: span ${width || 12};
    }
  `};
`;

const StyledThankYouContainer = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${space.small};
  `};
`;

const StyledTickCircle = styled(TickCircle)`
  width: 64px;
  height: 64px;
`;
const StyledCrossCircle = styled(CrossCircleOutline)`
  width: 64px;
  height: 64px;
`;

const StyledActions = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    justify-content: space-between;
    gap: ${space.medium};

    @media (max-width: ${maxMedia.medium}) {
      flex-direction: column;
      gap: ${space.small};
    }
  `};
`;

// JSX

// Umbraco form

export const UmbracoForm: FC<UmbracoFormProps> = ({ formData }) => {
  const [submitError, setSubmitError] = useState<FormErrors | undefined>();
  const [formSent, setFormSent] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [firstFieldClicked, setFirstFieldClicked] = useState(false);
  const { dataLayerPush } = useDataLayerPush();
  const { pageID } = useContext(PageContext)!;
  const pageCount = (formData?.pages ?? []).length;
  const lastPage = pageCount - 1;
  const { executeReCaptcha } = useGoogleReCaptchaV3({
    siteKey: process.env.GATSBY_PUBLIC_RECAPTCHA_KEY || '',
  });

  const initialData = useMemo(() => {
    const initialDataBase: FormValues = (formData?.pages ?? [])
      .flatMap((page) => page.fieldsets ?? [])
      .flatMap((fieldset) => fieldset.columns ?? [])
      .flatMap((column) => column.fields ?? [])
      .filter(
        (field) =>
          // remove fields that are not data fields // e.g. Title and description
          field?.alias &&
          field?.type?.name &&
          !['Title and description', 'Rich text'].includes(field.type.name)
      )
      .reduce((acc: FormValues, field) => {
        if (field?.alias) {
          acc[field.alias] =
            field?.type?.name === 'Multiple choice'
              ? []
              : field?.settings?.defaultValue || '';
        }
        return acc;
      }, {});

    return initialDataBase;
  }, [formData?.pages]);

  // Hande first click data layer

  const handleFieldFocus = () => {
    if (!firstFieldClicked) {
      setFirstFieldClicked(true);
    }
  };

  useEffect(() => {
    if (firstFieldClicked) {
      dataLayerPush({
        content_group: 'brochureware pages',
        event: 'form_start',
        form_id: '',
        form_name: formData.name,
      });
    }
  }, [dataLayerPush, firstFieldClicked, formData.name]);

  // Hook form

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialData,
  });

  const { register, handleSubmit, watch, formState, control, trigger } =
    formMethods;

  const { isSubmitting, isSubmitted } = formState;

  const { errors, dirtyFields } = useFormState({
    control,
  });

  // Helper functions

  const renderCondition = (
    condition?: umbracoForm['schemas']['FormConditionDto']
  ): boolean => {
    // Assume the thing should be shown if there are no conditions
    if (!condition) {
      return true;
    }

    const { actionType, rules, logicType } = condition;

    type CheckValue = string | number | readonly string[] | null | undefined;
    type RuleValue = string | boolean;
    const operators = {
      Is: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue === ruleValue,
      IsNot: (checkValue: CheckValue, ruleValue: RuleValue) =>
        !checkValue || checkValue === ruleValue,
      GreaterThen: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue && checkValue > ruleValue,
      LessThen: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue && checkValue < ruleValue,
      Contains: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue?.toString().includes(ruleValue.toString()),
      ContainsIgnoreCase: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue
          ?.toString()
          .toLowerCase()
          .includes(ruleValue?.toString().toLowerCase()),
      StartsWith: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue?.toString().startsWith(ruleValue?.toString()),
      StartsWithIgnoreCase: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue
          ?.toString()
          .toLowerCase()
          .startsWith(ruleValue?.toString().toLowerCase()),
      EndsWith: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue?.toString().endsWith(ruleValue?.toString()),
      EndsWithIgnoreCase: (checkValue: CheckValue, ruleValue: RuleValue) =>
        checkValue
          ?.toString()
          .toLowerCase()
          .endsWith(ruleValue?.toString().toLowerCase()),
      NotContains: (checkValue: CheckValue, ruleValue: RuleValue) =>
        !checkValue?.toString().includes(ruleValue?.toString()),
      NotContainsIgnoreCase: (checkValue: CheckValue, ruleValue: RuleValue) =>
        !checkValue
          ?.toString()
          .toLowerCase()
          .includes(ruleValue?.toString().toLowerCase()),
      NotStartsWith: (checkValue: CheckValue, ruleValue: RuleValue) =>
        !checkValue?.toString().startsWith(ruleValue?.toString()),
      NotStartsWithIgnoreCase: (checkValue: CheckValue, ruleValue: RuleValue) =>
        !checkValue
          ?.toString()
          .toLowerCase()
          .startsWith(ruleValue?.toString().toLowerCase()),
      NotEndsWith: (checkValue: CheckValue, ruleValue: RuleValue) =>
        !checkValue?.toString().endsWith(ruleValue?.toString()),
      NotEndsWithIgnoreCase: (checkValue: CheckValue, ruleValue: RuleValue) =>
        !checkValue
          ?.toString()
          .toLowerCase()
          .endsWith(ruleValue?.toString().toLowerCase()),
    } as const;

    const evaluateCondition = (
      operator: keyof typeof operators,
      checkValue: CheckValue,
      ruleValue: RuleValue
    ): boolean => {
      const operation = operators[operator];
      return operation ? Boolean(operation(checkValue, ruleValue)) : false;
    };

    const lookUpAliasFromId = (id?: string) => {
      if (!id) {
        return null;
      }
      return (formData.pages ?? [])
        .flatMap((page) => page.fieldsets ?? [])
        .flatMap((fieldset) => fieldset.columns ?? [])
        .flatMap((column) => column.fields ?? [])
        .find((field) => field.id === id)?.alias;
    };

    // Function to evaluate a single rule
    const evaluateRule = (rule: Record<string, string>) => {
      // Determine the rule value based on its string representation
      const ruleValue = (() => {
        switch (rule.value) {
          case 'true':
            return true;
          case 'false':
            return false;
          default:
            return rule.value;
        }
      })();

      // Look up the alias for the field ID in the rule
      const aliasFromId = lookUpAliasFromId(rule.field);

      // Get the value from form using the alias
      const checkValue = aliasFromId ? watch(aliasFromId) : null;

      const operator =
        rule.operator as umbracoForm['schemas']['FieldConditionRuleOperator'];

      /* console.log('Debug con: Rule:', rule);
      console.log(`Debug con: Value for ${aliasFromId}:`, checkValue); */

      return evaluateCondition(operator, checkValue, ruleValue);
    };

    // Determine if the field should be shown based on the logicType and rules
    const shouldShowField =
      logicType === 'All'
        ? rules?.every(evaluateRule)
        : rules?.some(evaluateRule);

    // Handle the actionType to either show or hide the field
    if (actionType === 'Show') {
      if (!shouldShowField) {
        return false; // Hide if the conditions are not met
      }
    } else if (actionType === 'Hide') {
      if (shouldShowField) {
        return false; // Hide field if the conditions are met
      }
    }

    return true;
  };

  // Form submission
  const onSubmit = async (data: Record<string, unknown>) => {
    setSubmitError(undefined);

    const token = await executeReCaptcha('submit');

    if (!token) {
      setSubmitError({
        title: 'Recaptcha Error',
        detail: 'Recaptcha verification failed',
      });

      return;
    }

    //  console.log('Form data before submission:', data);

    try {
      // Check if we have any file uploads in the form
      const hasFileUploads = Object.values(data).some(
        (value) => value instanceof FileList && value.length > 0
      );

      // console.log('Form contains file uploads:', hasFileUploads);

      // Prepare FormData to send
      const formToSend = new FormData();

      // Add form ID and metadata
      formToSend.append('__formId', formData.formGuid);
      formToSend.append('__referringPage', pageID || 'unknown');
      formToSend.append('__g-recaptcha-response', token);

      // Process form fields
      Object.entries(data).forEach(([key, value]) => {
        if (value instanceof FileList) {
          // Handle file uploads - directly append each file with the field name
          if (value.length > 0) {
            Array.from(value).forEach((file, index) => {
              /* console.log(
                `Adding file ${file.name} to form data for field ${key}`
              ); */
              // Use the same field name for all files - the server will handle them as an array
              formToSend.append(key, file);
            });
          }
        } else if (Array.isArray(value)) {
          // Handle arrays (e.g., multiple choice fields)
          if (value.length > 0) {
            value.forEach((item) => {
              if (item !== undefined && item !== null) {
                /* console.log(
                  `Adding array item ${item} to form data for field ${key}`
                ); */
                formToSend.append(key, String(item));
              }
            });
          } else {
            // Add empty field with empty string to ensure field is included
            formToSend.append(key, '');
          }
        } else if (value !== undefined && value !== null) {
          // Handle other field types
          //  console.log(`Adding field ${key} with value ${value} to form data`);
          formToSend.append(key, String(value));
        } else {
          // Add empty fields to ensure they're included in the form
          formToSend.append(key, '');
        }
      });

      // Log what's being sent for debugging
      // console.log('Form being submitted with these fields:');
      /* for (const pair of formToSend.entries()) {
        if (pair[1] instanceof File) {
          //  console.log(`${pair[0]}: [File ${(pair[1] as File).name}]`);
        } else {
          console.log(
            `${pair[0]}: ${String(pair[1]).substring(0, 50)}${
              String(pair[1]).length > 50 ? '...' : ''
            }`
          );
        }
      } */

      // Use fetch to send the form with appropriate headers
      const response = await fetch('/.netlify/functions/sendEmail', {
        method: 'POST',
        body: formToSend,
        // Important: Don't set Content-Type header, let the browser set it with the boundary
      });

      // console.log('Response received, status:', response.status);

      if (response.ok) {
        // console.log('Form submission successful');
        try {
          const responseData = (await response.json()) as FormDataResponse;
          /* console.log('Form submission response:', responseData); */

          if (responseData.error) {
            console.error(
              'Form submission returned error:',
              responseData.error
            );
            setSubmitError(responseData.error);
            throw responseData.error;
          } else {
            setFormSent(true);
          }
        } catch (e) {
          console.error('Error parsing response:', e);
          setSubmitError({
            title: 'Response Error',
            detail: 'Could not parse server response',
          });
          throw e;
        }
      } else {
        console.error('Form submission failed with status:', response.status);
        try {
          const errorResponse = await response.json();
          console.error('Error response:', errorResponse);

          if (typeof errorResponse === 'object') {
            const errorMMessage = Object.keys(errorResponse).length
              ? Object.entries(errorResponse)
                  .map((e) => {
                    // get the field name and error message
                    const [fieldName, errorMessage] = e;

                    return `${fieldName}: ${errorMessage}`;
                  })
                  .join(', ')
              : 'Unknown error occurred';
            setSubmitError({
              title: 'Form submission failed',
              detail: errorMMessage,
            });
          } else {
            setSubmitError(
              errorResponse.error || {
                title: 'Form submission error',
                detail: `Server returned an error: ${response.status}`,
              }
            );
          }
        } catch (e) {
          setSubmitError({
            title: 'Form submission error',
            detail: `Server returned an error: ${response.status}`,
          });
        }
        throw new Error(`HTTP error ${response.status}`);
      }
    } catch (error) {
      console.error('Error during form submission:', error);
      console.error(typeof error);

      // errors some as array of object field name and error, we need to map it to a string
      if (error instanceof Error) {
        setSubmitError({
          title: 'Form submission failed',
          detail: error.message || 'Unknown error occurred',
        });
      } else if (typeof error === 'string') {
        setSubmitError({
          title: 'Form submission failed',
          detail: error || 'Unknown error occurred',
        });
      } else if (Array.isArray(error)) {
        const errorMMessage = Array.isArray(error)
          ? error
              .map((e) => {
                // get the field name and error message
                const [fieldName, errorMessage] = Object.entries(e)[0];

                return `${fieldName}: ${errorMessage}`;
              })
              .join(', ')
          : 'Unknown error occurred';
        setSubmitError({
          title: 'Form submission failed',
          detail: errorMMessage,
        });
      } else {
        setSubmitError({
          title: 'Form submission failed',
          detail: 'Unknown error occurred',
        });
      }
    }
  };

  // Form navigation

  const getVisibleFields = () =>
    (formData?.pages?.[currentPage]?.fieldsets ?? [])
      .flatMap((fieldset) =>
        renderCondition(fieldset.condition)
          ? fieldset.columns?.flatMap((column) =>
              column?.fields
                ?.filter((field) => renderCondition(field.condition))
                .map((field) => field.alias)
            )
          : []
      )
      .filter(Boolean);

  const handleNextPage = async () => {
    const visiblePageFields = getVisibleFields() as string[];
    const isPageFieldsValid = await trigger(visiblePageFields);
    if (!isPageFieldsValid) return;

    setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    setCurrentPage((curPage) => curPage - 1);
  };

  // DataLayer errors

  const handleDataLayerErrors = (errorFields: FieldValues) => {
    Object.keys(errorFields).forEach((key) => {
      const err = errorFields[key];
      dataLayerPush({
        event: 'form_error',
        form_id: '',
        form_name: formData.name,
        interactionType: `error on: ${key}`,
        errorMessage: err.type === 'required' ? 'required' : err.message,
      });
    });
  };

  const onError = (errorFields: FieldValues) => {
    handleDataLayerErrors(errorFields);
  };

  // Datalyer form success - TODO - this needs to only fire if the API returns a success

  useEffect(() => {
    if (isSubmitted) {
      dataLayerPush({
        content_group: 'brochureware pages',
        event: 'form_submit',
        form_id: '',
        form_name: formData.name,
        errorMessage: null,
      });
    }
  }, [dataLayerPush, formData.name, isSubmitted]);

  // Render field

  const renderField = (field: umbracoForm['schemas']['FormFieldDto']) => {
    const {
      caption,
      helpText,
      type,
      required,
      settings,
      preValues,
      pattern,
      patternInvalidErrorMessage,
      alias = 'NOSET',
    } = field;

    const id = field.id || field.alias;
    if (!id || !renderCondition(field.condition)) {
      return null;
    }

    // Validation

    const invalid = !!errors[alias];
    const valid = dirtyFields[alias] && !errors[alias];
    const errorMessage: string | undefined =
      errors?.[alias]?.type === 'required'
        ? 'Required'
        : errors?.[alias]?.message || undefined;

    // Options

    switch (type?.name) {
      case 'Hidden':
        return (
          <Input
            key={id}
            {...register(alias, {
              required,
            })}
            label={caption ?? ''}
            hideLabel
            type="hidden"
            id={alias}
            defaultValue={settings?.defaultValue ?? undefined}
            required={required}
          />
        );
      case 'Password':
      case 'Short answer':
        return (
          <Input
            key={id}
            {...register(alias, {
              required: {
                value: required as boolean,
                message:
                  field?.requiredErrorMessage || 'This field is required',
              },
              pattern: pattern
                ? {
                    value: new RegExp(pattern),
                    message: patternInvalidErrorMessage || 'Invalid format',
                  }
                : undefined,
            })}
            invalid={invalid}
            valid={valid}
            label={caption ?? ''}
            type={
              type?.name === 'Password'
                ? 'password'
                : (settings?.fieldType as InputProps['type'])
            }
            id={alias}
            placeholder={settings?.placeholder ?? undefined}
            defaultValue={settings?.defaultValue ?? undefined}
            maxLength={
              settings?.maximumLength
                ? parseInt(settings.maximumLength, 10)
                : undefined
            }
            required={required}
            hideLabel={settings?.showLabel === 'False'}
            hintText={helpText || undefined}
            errorMessage={errorMessage}
            onFocus={handleFieldFocus}
          />
        );

      case 'Long answer':
        return (
          <Textarea
            key={id}
            {...register(alias, {
              required: {
                value: required as boolean,
                message:
                  field?.requiredErrorMessage || 'This field is required',
              },
              pattern: pattern
                ? {
                    value: new RegExp(pattern),
                    message: patternInvalidErrorMessage || 'Invalid format',
                  }
                : undefined,
            })}
            invalid={invalid}
            valid={valid}
            label={caption ?? ''}
            id={alias}
            placeholder={settings?.placeholder ?? undefined}
            defaultValue={settings?.defaultValue ?? undefined}
            maxLength={
              settings?.maximumLength
                ? parseInt(settings.maximumLength, 10)
                : undefined
            }
            rows={
              settings?.numberOfRows ? parseInt(settings.numberOfRows, 10) : 4
            }
            required={required}
            hideLabel={settings?.showLabel === 'False'}
            hintText={helpText || undefined}
            errorMessage={errorMessage}
            onFocus={handleFieldFocus}
          />
        );
      case 'Data Consent':
      case 'Checkbox':
        return (
          <Stack gap="xSmall">
            <Checkbox
              id={alias}
              {...register(alias, {
                required,
              })}
              label={
                type?.name === 'Data Consent' && settings?.acceptCopy
                  ? settings?.acceptCopy
                  : caption
              }
              invalid={invalid}
              valid={valid}
              errorMessage={errorMessage}
              //  onFocus={handleFieldFocus}
            />
            {helpText && (
              <Typography component="p" variant="bodyMedium">
                {helpText}
              </Typography>
            )}
          </Stack>
        );
      case 'Single choice': {
        const singleOptions = preValues?.map((preValue) => ({
          value: preValue.value,
          name: preValue.caption || preValue.value,
        }));

        return (
          <Controller
            name={alias}
            rules={{ required }}
            render={({ field: controllerField }) => (
              <ButtonSelect
                label={caption ?? ''}
                id={alias}
                invalid={invalid}
                hideLabel={settings?.showLabel === 'False'}
                hintText={helpText || undefined}
                valid={valid}
                defaultValue={settings?.defaultValue ?? undefined}
                options={singleOptions as ButtonSelectOptions[]}
                errorMessage={errorMessage}
                placeholder={settings?.placeholder ?? undefined}
                {...controllerField}
              />
            )}
          />
        );
      }
      case 'Multiple choice': {
        const multipleChoiceOptions = preValues?.map((preValue) => ({
          value: preValue.value,
          name: preValue.caption || preValue.value,
        }));

        return (
          <FormField
            label={caption || ''}
            invalid={invalid}
            errorMessage={errorMessage}
          >
            <Stack gap="xSmall">
              {multipleChoiceOptions?.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`${id}_${option.value as string}`}
                  {...register(alias, {
                    required,
                  })}
                  value={option.value}
                  label={option.name}
                  invalid={invalid}
                />
              ))}
            </Stack>
          </FormField>
        );
      }
      case 'Dropdown': {
        const dropdownOptions = preValues?.map((preValue) => ({
          value: preValue.value,
          name: preValue.caption || preValue.value,
        }));

        return (
          <Controller
            name={alias}
            rules={{ required }}
            control={control}
            render={({ field }) => (
              <FilterInput
                id={alias}
                label={caption || ''}
                placeholder={settings?.placeholder ?? undefined}
                defaultValue={
                  dropdownOptions?.find(
                    (option) => option.value === settings?.defaultValue
                  )?.name || 'Please choose one...'
                }
                disableFilter
                options={dropdownOptions}
                invalid={invalid}
                valid={valid}
                hideLabel={settings?.showLabel === 'False'}
                hintText={helpText || undefined}
                onFocus={handleFieldFocus}
                errorMessage={errorMessage}
                {...field}
              />
            )}
          />
        );
      }
      case 'Title and description': {
        return (
          <Stack gap="xSmall">
            <Typography
              component={(settings?.captionTag as TypographyComponents) || 'h3'}
              variant="headingXLarge"
            >
              {caption}
            </Typography>
            {settings?.bodyText && <Html large>{settings.bodyText}</Html>}
          </Stack>
        );
      }
      case 'File upload': {
        const uploadAcceptedFileTypes =
          field?.fileUploadOptions?.allowAllUploadExtensions === false
            ? field?.fileUploadOptions?.allowedUploadExtensions
                ?.map((value) => `.${value}`)
                .join(',')
            : undefined;

        return (
          <Input
            key={id}
            {...register(alias, {
              required,
            })}
            invalid={invalid}
            valid={valid}
            label={caption ?? ''}
            type="file"
            id={alias}
            multiple={field?.fileUploadOptions?.allowMultipleFileUploads}
            {...(uploadAcceptedFileTypes && {
              accept: uploadAcceptedFileTypes,
            })}
            placeholder={settings?.placeholder ?? undefined}
            defaultValue={settings?.defaultValue ?? undefined}
            maxLength={
              settings?.maximumLength
                ? parseInt(settings.maximumLength, 10)
                : undefined
            }
            required={required}
            hideLabel={settings?.showLabel === 'False'}
            hintText={`${helpText || ''}${
              uploadAcceptedFileTypes
                ? ` Accepted file types: ${uploadAcceptedFileTypes}`
                : ''
            }`}
            onFocus={handleFieldFocus}
            errorMessage={errorMessage}
          />
        );
      }
      case 'Custom Date Picker':
      case 'Date': {
        return (
          <div>
            <Controller
              control={control}
              name={alias}
              render={({ field }) => (
                <InlineCalendar
                  previousDays={9999999999999} // ??
                  errorMessage={errorMessage}
                  hintText={helpText as string}
                  id={alias}
                  invalid={invalid}
                  label={caption ?? ''}
                  preventBankHolidaysAsSelectable={
                    settings?.preventBankHolidaysAsSelectable === 'True'
                  }
                  preventWeekendsAsSelectable={
                    settings?.preventWeekendsAsSelectable === 'True'
                  }
                  allowAnyFutureDate
                  allowAnyPastDate
                  {...field}
                />
              )}
            />
          </div>
        );
      }
      case 'Rich text':
        return settings?.html && <Html large>{settings?.html}</Html>;
      default:
        return (
          <div key={id}>
            <b>Unknown field type:</b> {type?.name} | {caption}
          </div>
        );
    }
  };

  if (!formData?.pages) {
    return null;
  }

  return (
    <StyledUmbracoForm>
      <FormProvider {...formMethods}>
        {isSubmitted && formSent ? (
          <Stack gap="xxLarge">
            <Stack gap="xSmall">
              <StyledThankYouContainer>
                <StyledTickCircle />
                <Typography component="h1" variant="headingLarge">
                  Thank you
                </Typography>
              </StyledThankYouContainer>
              <Typography variant="bodyLarge">
                {formData.messageOnSubmit}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            method="post"
            noValidate
            encType="multipart/form-data"
          >
            <Stack>
              {/* Pages */}
              {formData?.pages.map((page, pageIndex) => (
                <div
                  key={pageIndex}
                  style={{
                    display: currentPage === pageIndex ? 'block' : 'none',
                  }}
                >
                  {page.caption && (
                    <Stack>
                      <Typography component="h2" variant="headingLarge">
                        {pageCount > 1 && (
                          <>
                            {currentPage + 1}/{pageCount} -
                          </>
                        )}
                        {page.caption}
                      </Typography>
                      {currentPage === 0 && (
                        <Typography>
                          Fields marked with * are required.
                        </Typography>
                      )}

                      <Divider />
                    </Stack>
                  )}

                  {/* Field sets */}
                  {page?.fieldsets?.map((fieldset, fieldSetIndex) => {
                    if (!fieldset || !renderCondition(fieldset.condition)) {
                      return null;
                    }
                    return (
                      <StyledFieldset key={fieldSetIndex} as="fieldset">
                        {/* Fieldset legend */}
                        {fieldset.caption && (
                          <Stack>
                            <Typography component="h3" variant="headingMedium">
                              {fieldset.caption}
                            </Typography>
                            {fieldSetIndex >= 1 && <Divider />}
                          </Stack>
                        )}

                        {/* Fieldset columns  */}

                        <StyledFieldsetColumns>
                          {fieldset?.columns?.map((column, columnIndex) => (
                            // Column
                            <StyleFieldsetColumn
                              key={columnIndex}
                              width={column.width}
                            >
                              {column?.caption && (
                                <Typography
                                  component="h3"
                                  variant="headingSmall"
                                >
                                  {column?.caption}
                                </Typography>
                              )}

                              {/* Fields */}

                              <Stack>
                                {column?.fields?.map((field, fieldIndex) => (
                                  <Fragment key={field?.id ?? fieldIndex}>
                                    {renderField(field)}
                                  </Fragment>
                                ))}
                              </Stack>
                            </StyleFieldsetColumn>
                          ))}
                        </StyledFieldsetColumns>
                      </StyledFieldset>
                    );
                  })}
                </div>
              ))}
              {renderCondition(formData?.pages[currentPage]?.condition) && (
                <StyledActions>
                  {/* Page navigation navigation  - Next / Previous */}
                  {formData?.pages &&
                    pageCount >= 2 &&
                    (!isSubmitted || submitError) && (
                      <>
                        {/* Previous button */}
                        {currentPage > 0 ? (
                          <Button
                            type="button"
                            arrowLeft
                            variant="primaryOutline"
                            onClick={handlePreviousPage}
                            disabled={isSubmitting}
                          >
                            {formData.previousLabel}
                          </Button>
                        ) : (
                          <div />
                        )}
                        {/* Next button */}
                        {currentPage < lastPage && (
                          <Button
                            type="button"
                            arrowRight
                            onClick={handleNextPage}
                          >
                            {formData.nextLabel}
                            {formData?.pages[currentPage]?.caption
                              ? `: ${formData?.pages[currentPage].caption}`
                              : ''}
                          </Button>
                        )}
                      </>
                    )}

                  {/* Submit button */}
                  {currentPage === lastPage && (
                    <Button
                      type="submit"
                      label={
                        isSubmitting
                          ? 'Submitting'
                          : formData.submitLabel || 'Submit'
                      }
                      disabled={isSubmitting}
                    />
                  )}
                </StyledActions>
              )}

              {submitError && (
                <Stack gap="xSmall">
                  <StyledThankYouContainer>
                    <StyledCrossCircle />
                    <Typography component="h1" variant="headingLarge">
                      {submitError.title ?? 'Error'}
                    </Typography>
                  </StyledThankYouContainer>
                  <Typography variant="bodyLarge">
                    {submitError.detail ??
                      'There was an error submitting the form'}
                  </Typography>
                </Stack>
              )}
              <Typography variant="bodySmall">
                This site is protected by reCAPTCHA and the Google{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>{' '}
                apply.
              </Typography>
            </Stack>
          </form>
        )}
      </FormProvider>
    </StyledUmbracoForm>
  );
};

export default UmbracoForm;
